import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ZonePicker } from '../climb-create/ZonePicker';
import { RoutesetterPicker } from '../routesetters/RoutesetterPicker';
import { climbScansService } from '../../services/climb-scans';
import { climbsService } from '../../services/climbs';
import { useToast } from '../ui/use-toast';
import type { ClimbScan, ClimbScanZone, ClimbScanRouteSetter } from '../../types/climb-scan';
import { climbTitle } from '../../utils/climb-title';
import type { Climb } from '../../types/climb';
import type { Zone } from '../../types/zone';
import type { Step2ZoneSummary } from '../climb-create/Step2Form';

interface Props {
  scan: ClimbScan;
  gymSlug: string;
  zones: Zone[];
  onScanUpdated: (scan: ClimbScan) => void;
}

function zoneToSummary(zone: Zone): Step2ZoneSummary {
  return { id: zone.id, name: zone.name, slug: zone.slug, image_small_url: zone.image_small_url };
}

function scanZoneToSummary(zone: ClimbScanZone): Step2ZoneSummary {
  return { id: zone.id, name: zone.name, slug: zone.slug, image_small_url: zone.image_small_url };
}

export function ScanDefaultsForm({ scan, gymSlug, zones, onScanUpdated }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const isComplete = !!scan.zone && (scan.route_setters ?? []).length > 0;
  const [open, setOpen] = useState(!isComplete ? 'settings' : '');

  const [zone, setZone] = useState<Step2ZoneSummary | null>(
    scan.zone ? scanZoneToSummary(scan.zone) : null
  );
  const [routeSetters, setRouteSetters] = useState<ClimbScanRouteSetter[]>(scan.route_setters ?? []);

  useEffect(() => {
    if (!!scan.zone && (scan.route_setters ?? []).length > 0) {
      setOpen('');
    }
  }, [scan.zone, scan.route_setters]);
  const [saving, setSaving] = useState(false);

  const savedRouteSetterIds = (scan.route_setters ?? []).map(r => r.id).sort().join(',');
  const currentRouteSetterIds = routeSetters.map(r => r.id).sort().join(',');
  const isDirty =
    (zone?.id ?? null) !== (scan.zone?.id ?? null) ||
    currentRouteSetterIds !== savedRouteSetterIds;

  const [zoneClimbsOpen, setZoneClimbsOpen] = useState(false);
  const [zoneClimbs, setZoneClimbs] = useState<Climb[]>([]);
  const [loadingZoneClimbs, setLoadingZoneClimbs] = useState(false);
  const [deletingClimbId, setDeletingClimbId] = useState<string | null>(null);

  async function handleOpenZoneClimbs() {
    if (!scan.location || !scan.zone) return;
    setZoneClimbsOpen(true);
    setLoadingZoneClimbs(true);
    try {
      const climbs = await climbsService.searchClimbs({
        location_id: scan.location.id,
        zone_id: scan.zone.id,
        type: scan.climb_type,
      });
      setZoneClimbs(climbs);
    } catch {
      toast({ title: t('climbScan.defaults.deleteClimbError'), variant: 'destructive' });
    } finally {
      setLoadingZoneClimbs(false);
    }
  }

  async function handleDeleteZoneClimb(climb: Climb) {
    if (!window.confirm(t('climbScan.defaults.deleteClimbConfirm', { name: climb.name }))) return;
    setDeletingClimbId(climb.id);
    try {
      await climbsService.destroyClimb(climb.id);
      setZoneClimbs(prev => prev.filter(c => c.id !== climb.id));
    } catch {
      toast({ title: t('climbScan.defaults.deleteClimbError'), variant: 'destructive' });
    } finally {
      setDeletingClimbId(null);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      const updated = await climbScansService.updateScan(gymSlug, scan.id, {
        zone_id: zone?.id ?? null,
        route_setter_ids: routeSetters.map(r => r.id),
      });
      onScanUpdated(updated);
      toast({ title: t('climbScan.defaults.saved') });
    } catch {
      toast({ title: t('climbScan.saveError'), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
    <Accordion type="single" collapsible value={open} onValueChange={setOpen}>
      <AccordionItem value="settings" className="border rounded-lg overflow-hidden">
        <AccordionTrigger className="px-4 py-3 text-sm font-medium hover:no-underline">
          <span className="flex items-center gap-2 flex-wrap">
            <span>{t('climbScan.defaults.title')}</span>
            {scan.zone && (
              <span className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground">
                {scan.zone.name}
              </span>
            )}
            {(scan.route_setters ?? []).map(r => (
              <span key={r.id} className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground">
                {r.name}
              </span>
            ))}
          </span>
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-4 space-y-3">
          {zones.length > 0 && (
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">{t('climbScan.defaults.zone')}</label>
              <ZonePicker
                zones={zones}
                value={zone}
                onChange={z => setZone(z ? zoneToSummary(zones.find(zn => zn.id === z.id)!) : null)}
              />
            </div>
          )}

          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">{t('climbScan.defaults.routeSetters')}</label>
            <RoutesetterPicker
              mode="multiple"
              value={routeSetters}
              onChange={setRouteSetters}
              locationId={gymSlug}
              placeholder={t('climbScan.color.routeSetterPlaceholder')}
            />
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <Button size="sm" variant="outline" onClick={handleSave} disabled={saving || !isDirty}>
              {saving ? t('common.loading') : t('climbScan.defaults.save')}
            </Button>
            {scan.zone && (
              <button
                type="button"
                className="text-xs text-primary underline hover:no-underline"
                onClick={handleOpenZoneClimbs}
              >
                {t('climbScan.defaults.viewExistingClimbs')}
              </button>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>

    <Dialog open={zoneClimbsOpen} onOpenChange={setZoneClimbsOpen}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('climbScan.defaults.existingClimbsTitle')}</DialogTitle>
        </DialogHeader>
        {loadingZoneClimbs ? (
          <p className="text-sm text-muted-foreground py-4 text-center">{t('common.loading')}</p>
        ) : zoneClimbs.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">{t('climbScan.defaults.noExistingClimbs')}</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 pt-2">
            {zoneClimbs.map(climb => (
              <div key={climb.id} className="relative rounded-lg overflow-hidden border border-border group">
                {climb.image_small_url ? (
                  <img
                    src={climb.image_small_url}
                    alt={climbTitle(climb)}
                    className="w-full aspect-[3/4] object-cover"
                  />
                ) : (
                  <div className="w-full aspect-[3/4] bg-muted flex items-center justify-center">
                    <span className="text-xs text-muted-foreground text-center px-2">{climb.name}</span>
                  </div>
                )}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-2">
                  <p className="text-white text-xs font-medium truncate mb-1">{climb.name}</p>
                  <button
                    type="button"
                    className="self-end p-1 rounded bg-destructive text-destructive-foreground hover:bg-destructive/80 disabled:opacity-50"
                    onClick={() => handleDeleteZoneClimb(climb)}
                    disabled={deletingClimbId === climb.id}
                    title={t('climbScan.color.cancel')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-1 bg-black/50 group-hover:hidden">
                  <p className="text-white text-xs truncate">{climb.name}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}
